import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { FolderKanban, Plus, Loader2, Archive, ArchiveRestore, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { invalidateProjectsCache } from './ProjectSelect';

export const ProjectsSettingsTab = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/projects`, { params: { status: 'all', include_stats: true } });
      setProjects(data.projects || []);
    } catch { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/projects`, { name: newName.trim(), created_from: 'settings' });
      if (data.existed) toast.info('A project with this name already exists');
      else toast.success(`Project "${data.project.name}" created`);
      setNewName('');
      invalidateProjectsCache();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create project'); }
    finally { setCreating(false); }
  };

  const rename = async (id) => {
    try {
      await axios.put(`${API}/projects/${id}`, { name: editName });
      toast.success('Project renamed');
      setEditingId(null);
      invalidateProjectsCache();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rename failed'); }
  };

  const toggleArchive = async (p) => {
    try {
      await axios.post(`${API}/projects/${p.id}/${p.status === 'archived' ? 'restore' : 'archive'}`);
      toast.success(p.status === 'archived' ? 'Project restored' : 'Project archived. Hidden from pickers, still in historical reports');
      invalidateProjectsCache();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const visible = projects.filter(p => showArchived || p.status === 'active');
  const archivedCount = projects.filter(p => p.status === 'archived').length;

  return (
    <Card data-testid="projects-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><FolderKanban className="w-5 h-5 text-[#108981]" /> Projects</CardTitle>
        <CardDescription>
          Track income and costs per project. Tag transactions anywhere on the platform: invoices, bills, bank transactions, journals, payroll. And see per-project results in your reports.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New project name (e.g. Lekki Site B)"
            onKeyDown={(e) => e.key === 'Enter' && create()} data-testid="new-project-name" />
          <Button onClick={create} disabled={creating || !newName.trim()} className="bg-[#108981] hover:bg-[#0D746D] text-white gap-1.5" data-testid="create-project-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
          </Button>
        </div>

        {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-300 mx-auto my-6" /> : (
          <div className="border rounded-xl divide-y" data-testid="projects-list">
            {visible.length === 0 && <p className="p-4 text-sm text-slate-400 text-center">No projects yet: create one above, or from any document as you work.</p>}
            {visible.map(p => (
              <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`project-row-${p.id}`}>
                <FolderKanban className={`w-4 h-4 flex-shrink-0 ${p.status === 'archived' ? 'text-slate-300' : 'text-[#108981]'}`} />
                <div className="flex-1 min-w-0">
                  {editingId === p.id ? (
                    <div className="flex items-center gap-1.5">
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 text-sm" autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && rename(p.id)} data-testid={`edit-project-name-${p.id}`} />
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" onClick={() => rename(p.id)}><Check className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <>
                      <p className={`text-sm font-medium truncate ${p.status === 'archived' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{p.name}</p>
                      <p className="text-xs text-slate-400">{p.journal_count || 0} transaction{(p.journal_count || 0) !== 1 ? 's' : ''}{p.created_from && p.created_from !== 'settings' ? ` · created from ${p.created_from.replace('_', ' ')}` : ''}</p>
                    </>
                  )}
                </div>
                {p.status === 'archived' && <Badge variant="secondary" className="text-[10px]">Archived</Badge>}
                {editingId !== p.id && p.status === 'active' && (
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400" onClick={() => { setEditingId(p.id); setEditName(p.name); }} data-testid={`rename-project-${p.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400" onClick={() => toggleArchive(p)}
                  title={p.status === 'archived' ? 'Restore' : 'Archive (hidden from pickers, kept in reports)'}
                  data-testid={`archive-project-${p.id}`}>
                  {p.status === 'archived' ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                </Button>
              </div>
            ))}
          </div>
        )}

        {archivedCount > 0 && (
          <button className="text-xs text-slate-400 hover:text-slate-600" onClick={() => setShowArchived(!showArchived)} data-testid="toggle-archived-projects">
            {showArchived ? 'Hide' : 'Show'} {archivedCount} archived project{archivedCount > 1 ? 's' : ''}
          </button>
        )}
      </CardContent>
    </Card>
  );
};
